/**
 * AppShell - the universal authed chrome.
 *
 * Provides the nav rail + global overlays (Cmd-K palette, Cmd-? cheat sheet,
 * Neo chat panel) + the outer flex container that shrinks when Neo opens.
 *
 * Use this on every authed page that doesn't use ThreePaneLayout (batches,
 * voice profiles, batch card stack, party detail, etc). ThreePaneLayout
 * itself wraps AppShell internally, so the three-pane pages still get the
 * same chrome with zero duplication.
 *
 * Children render in the main content area. The shell handles:
 *  - paddingRight = NEO_PANEL_WIDTH when Neo open (so content shifts left)
 *  - Cmd-K + Cmd-? + plain ? global shortcuts
 *  - Neo state lifted here so the nav rail button can toggle it
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'wouter';
import { useEmbeddedMode } from '../../hooks/useEmbeddedMode.js';
import { forwardCommandPaletteToShell } from '../../lib/shell-command-palette.js';
import NavRail from './nav-rail.js';
import MobileNav from './mobile-nav.js';
import TopRibbon from './top-ribbon.js';
import CmdPalette from './cmd-palette.js';
import ShortcutCheatSheet from './shortcut-cheat-sheet.js';
import NeoChatPanel from '../neo/NeoChatPanel.js';
import EmployeeChatPanel, { type EmployeeChatTarget } from '../ai-employees/EmployeeChatPanel.js';
import ActiveCallBanner from '../calls/ActiveCallBanner.js';
import { postToShell, subscribeToShell, notifyShellNavigation } from '../../lib/shell-bridge.js';
import { useIsMobileViewport, useIsNarrowViewport } from '../../hooks/useMediaQuery.js';

const NEO_PANEL_WIDTH = 360;
const EMPLOYEE_CHAT_PANEL_WIDTH = 360;

/**
 * Default top-ribbon label derived from the route. Pages with a richer label
 * (a deal's name) override it by dispatching a `poe:context-label` CustomEvent;
 * the override clears on navigation so it never goes stale.
 */
function routeLabel(path: string): string {
  if (path === '/' || path.startsWith('/inbox')) return 'Inbox';
  if (path.startsWith('/deals')) return 'Deals';
  if (path.startsWith('/planner') || path.startsWith('/calendar')) return 'Planner';
  if (
    path.startsWith('/ai-workforce') ||
    path.startsWith('/ai-employees') ||
    path.startsWith('/voice-profiles') ||
    path.startsWith('/batches')
  ) return 'AI Workforce';
  if (path.startsWith('/parties') || path.startsWith('/contacts')) return 'Contacts';
  if (path.startsWith('/connected-accounts') || path.startsWith('/accounts')) return 'Accounts';
  if (path.startsWith('/settings')) return 'Settings';
  return 'Personal Outreach Engine';
}

export interface AppShellProps {
  children: React.ReactNode;
  /** Show the list-collapse chevron on the nav rail (three-pane pages only). */
  onToggleList?: () => void;
  /** Current collapse state of the list pane (controls chevron direction). */
  listCollapsed?: boolean;
  /** Optional icon action shown in the compact mobile top bar. */
  mobileContextAction?: React.ReactNode;
  /** Override the outer container className. */
  className?: string;
  /**
   * Render the content area alone: no nav rail, no top ribbon, no Neo panel,
   * no command palette, no keyboard shortcuts.
   *
   * This exists for the embedded inbox. When POE runs inside LA-AI's iframe,
   * the host already provides navigation, search and an assistant, so shipping
   * ours produces a second brand header, a second global search and a second
   * Neo inside someone else's app. It is also not merely cosmetic: the ribbon's
   * global search reaches deals and contacts well outside the inbox the embed
   * is scoped to, and Note / Call / Neo are ACTION surfaces sitting on a
   * read-only `inbox:read` bridge that hard-405s every non-GET, so they are
   * controls that cannot work.
   *
   * Standalone POE is unaffected.
   */
  chromeless?: boolean;
}

export default function AppShell({
  children,
  onToggleList,
  listCollapsed,
  mobileContextAction,
  className = '',
  chromeless = false,
}: AppShellProps) {
  const [cmdOpen, setCmdOpen] = useState(false);
  const [cheatSheetOpen, setCheatSheetOpen] = useState(false);
  const [neoOpenState, setNeoOpenState] = useState<boolean>(() => {
    try { return localStorage.getItem('poe-neo-panel-open') === '1'; } catch { return false; }
  });
  const [neoContext, setNeoContext] = useState<Record<string, unknown>>({});
  const [employeeChats, setEmployeeChats] = useState<EmployeeChatTarget[]>([]);
  const [location] = useLocation();
  const [labelOverride, setLabelOverride] = useState<string | null>(null);
  const contextLabel = labelOverride ?? routeLabel(location);
  const isMobile = useIsMobileViewport();
  const isNarrow = useIsNarrowViewport();
  // Only consulted by the chromeless Cmd-K forward below. Pessimistic until the
  // host completes the shell:hello handshake, so standalone POE never forwards.
  const embedded = useEmbeddedMode();
  const maxEmployeeChats = isMobile || isNarrow ? 1 : 2;
  const setNeoOpen = useCallback((value: boolean | ((current: boolean) => boolean)) => {
    setNeoOpenState((current) => {
      const next = typeof value === 'function' ? value(current) : value;
      try { localStorage.setItem('poe-neo-panel-open', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  }, []);
  const neoOpen = neoOpenState;
  const employeePanelWidth = isMobile ? window.innerWidth : EMPLOYEE_CHAT_PANEL_WIDTH;
  const employeePanelCount = employeeChats.length;
  const desktopNeoWidth = neoOpen && !isMobile ? NEO_PANEL_WIDTH : 0;
  const desktopEmployeeWidth = !isMobile ? employeePanelCount * EMPLOYEE_CHAT_PANEL_WIDTH : 0;
  const desktopRightRailWidth = desktopNeoWidth + desktopEmployeeWidth;
  // On mobile the Neo panel goes full-screen so it doesn't share width with
  // the main content. On desktop it stays at NEO_PANEL_WIDTH and shifts
  // content via paddingRight.
  const effectiveNeoWidth = isMobile ? window.innerWidth : NEO_PANEL_WIDTH;

  // Global keyboard shortcuts (Cmd-K palette + Cmd-? cheat sheet + plain ?)
  // Chromeless mode binds nothing: inside a host app these keys belong to the
  // host, and an iframe silently swallowing Cmd-K is worse than not having it.
  useEffect(() => {
    if (chromeless) return;
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isTyping = target && (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      );
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCmdOpen((o) => !o);
      } else if ((e.metaKey || e.ctrlKey) && (e.key === '?' || (e.shiftKey && e.key === '/'))) {
        e.preventDefault();
        setCheatSheetOpen((o) => !o);
      } else if (e.key === '?' && !isTyping && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setCheatSheetOpen((o) => !o);
      } else if ((e.metaKey || e.ctrlKey) && e.key === '/' && !e.shiftKey) {
        // Cmd-/ opens Neo (Cmd-Shift-/ = Cmd-? is the cheat sheet above).
        e.preventDefault();
        setNeoOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [chromeless]);

  /**
   * Chromeless only: hand Cmd-K to the host instead of swallowing it.
   *
   * The effect above binds nothing when chromeless because "these keys belong
   * to the host" - which was right, but left Cmd-K dead inside LA-AI's External
   * inbox. We are a CROSS-ORIGIN iframe there, so our keydown never reaches
   * LA-AI's palette on its own; the host cannot read keys in our document, and
   * this hands over the one key it wants. It delivers what that comment always
   * intended.
   *
   * Deliberately a separate effect rather than a branch in the one above, so
   * the standalone path is untouched: when chromeless is false this never
   * binds, POE's own palette works exactly as today, and nothing is posted
   * anywhere. `forwardCommandPaletteToShell` is additionally gated on the
   * shell:hello handshake, so framing POE without being LA-AI gets nothing.
   *
   * Cmd-K only. Cmd-?, plain ? and Cmd-/ stay unbound in chromeless mode, as
   * before - the host owns its own shortcuts and we are not guessing which.
   *
   * preventDefault only on a real forward, so a frame that did nothing with the
   * key does not also steal the browser's native Cmd-K.
   */
  useEffect(() => {
    if (!chromeless) return;
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        if (forwardCommandPaletteToShell(embedded)) e.preventDefault();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [chromeless, embedded.isEmbedded, embedded.parentOrigin]);

  const toggleNeo = useCallback(() => setNeoOpen((o) => !o), []);

  // Expose workspace offsets as CSS variables so global overlays (Cmd-K, cheat
  // sheet, etc.) can center themselves within the workspace area rather than
  // the raw viewport. --workspace-left = nav rail width. --workspace-right-pad
  // = 0 when Neo closed, NEO_PANEL_WIDTH when open.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--workspace-left', isMobile ? '0px' : '56px');
    // On mobile, the Neo panel goes full-screen so we don't apply a right
    // pad - the main content is hidden behind the overlay instead.
    const rightPad = desktopRightRailWidth > 0 ? `${desktopRightRailWidth}px` : '0px';
    root.style.setProperty('--workspace-right-pad', rightPad);
    return () => {
      root.style.removeProperty('--workspace-left');
      root.style.removeProperty('--workspace-right-pad');
    };
  }, [desktopRightRailWidth, isMobile]);

  // Shell bridge (Phase 5 item #45). Active only when iframed inside a host
  // shell; no-op when standalone. Announces readiness on mount, listens for
  // navigate / set-tenant messages, and reports navigation back upward.
  useEffect(() => {
    postToShell({ type: 'shell:poe-ready' });
    const unsub = subscribeToShell((msg) => {
      if (msg.type === 'shell:navigate' && typeof msg.path === 'string') {
        window.location.href = msg.path;
      }
      // shell:set-tenant is reserved for a future multi-tenant switcher; for
      // now we ignore it (POE's session already binds the tenant).
    });
    notifyShellNavigation(window.location.pathname);
    return unsub;
  }, []);

  useEffect(() => {
    const onNeoContext = (event: Event) => {
      const detail = (event as CustomEvent<Record<string, unknown> | null>).detail;
      setNeoContext(detail && typeof detail === 'object' ? detail : {});
    };
    window.addEventListener('poe:neo-context', onNeoContext);
    return () => window.removeEventListener('poe:neo-context', onNeoContext);
  }, []);

  // Top-ribbon context label: pages dispatch `poe:context-label` with a richer
  // name (e.g. a deal title); the override clears on navigation so a stale
  // label never lingers after leaving the page that set it.
  useEffect(() => {
    const onLabel = (event: Event) => {
      const detail = (event as CustomEvent<string | null>).detail;
      setLabelOverride(typeof detail === 'string' && detail.trim() ? detail : null);
    };
    window.addEventListener('poe:context-label', onLabel);
    return () => window.removeEventListener('poe:context-label', onLabel);
  }, []);

  // Employee chat: the context-zone roster dispatches `poe:open-employee-chat`
  // with the clicked employee. Desktop can keep two employees side-by-side
  // beside Neo; tablet/phone keeps one current employee so the rail never crowds
  // the workspace.
  useEffect(() => {
    const onOpenEmployee = (event: Event) => {
      const detail = (event as CustomEvent<EmployeeChatTarget | null>).detail;
      if (detail && typeof detail === 'object' && typeof detail.id === 'string') {
        if (isNarrow) setNeoOpen(false);
        setEmployeeChats((current) => {
          const existing = current.filter((employee) => employee.id !== detail.id);
          return [...existing, detail].slice(-maxEmployeeChats);
        });
      }
    };
    window.addEventListener('poe:open-employee-chat', onOpenEmployee);
    return () => window.removeEventListener('poe:open-employee-chat', onOpenEmployee);
  }, [isNarrow, maxEmployeeChats, setNeoOpen]);

  useEffect(() => {
    setEmployeeChats((current) => current.slice(-maxEmployeeChats));
  }, [maxEmployeeChats]);

  const closeEmployeeChat = useCallback((employeeId: string) => {
    setEmployeeChats((current) => current.filter((employee) => employee.id !== employeeId));
  }, []);

  useEffect(() => {
    setLabelOverride(null);
  }, [location]);

  // The embedded inbox: content only. No rail, no ribbon, no overlays, and no
  // right-rail padding (there is no Neo panel to make room for). The <main>
  // landmark is kept so the skip-link anchor and screen-reader structure hold.
  if (chromeless) {
    return (
      <div className={`flex h-screen overflow-hidden bg-surface ${className}`}>
        <main id="main-content" role="main" tabIndex={-1} className="flex-1 overflow-hidden focus:outline-none">
          {children}
        </main>
      </div>
    );
  }

  return (
    <div
      className={`safe-top-offset flex h-screen overflow-hidden bg-surface ${className}`}
      style={{
        paddingRight: !isMobile ? desktopRightRailWidth : 0,
        transition: 'padding-right 0.22s cubic-bezier(0.16, 1, 0.3, 1)',
      }}
    >
      {isMobile ? (
                <MobileNav
                  onOpenCommandPalette={() => setCmdOpen(true)}
                  onToggleNeo={toggleNeo}
                  neoOpen={neoOpen}
                  mobileContextAction={mobileContextAction}
                />
      ) : (
        <NavRail
          onToggleList={onToggleList}
          listCollapsed={listCollapsed}
          onToggleNeo={toggleNeo}
          neoOpen={neoOpen}
        />
      )}

      {/* Workspace column: the desktop top ribbon sits above the main content
          area; on mobile it is hidden (the mobile nav carries search + Neo).
          The landmark + App.tsx skip-link anchor stay on <main>. */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* In-flow so an active call pushes the ribbon + content down instead
            of painting over the TopRibbon. Persists across routes (shell-level). */}
        <ActiveCallBanner />
        {!isMobile && (
          <TopRibbon
            contextLabel={contextLabel}
            onOpenSearch={() => setCmdOpen(true)}
            onToggleNeo={toggleNeo}
            neoOpen={neoOpen}
            contextAction={mobileContextAction}
          />
        )}
        <main id="main-content" role="main" tabIndex={-1} className="flex-1 overflow-hidden focus:outline-none">
          {children}
        </main>
      </div>

      {/* Global overlays + Neo panel */}
      <CmdPalette open={cmdOpen} onClose={() => setCmdOpen(false)} />
      <ShortcutCheatSheet open={cheatSheetOpen} onClose={() => setCheatSheetOpen(false)} />
      <NeoChatPanel context={neoContext} open={neoOpen} onOpenChange={setNeoOpen} width={effectiveNeoWidth} />
      {employeeChats.map((employee, index) => (
        <EmployeeChatPanel
          key={employee.id}
          employee={employee}
          open
          width={employeePanelWidth}
          rightOffset={!isMobile ? desktopNeoWidth + (employeeChats.length - 1 - index) * EMPLOYEE_CHAT_PANEL_WIDTH : 0}
          onClose={() => closeEmployeeChat(employee.id)}
        />
      ))}
    </div>
  );
}

export { NEO_PANEL_WIDTH, EMPLOYEE_CHAT_PANEL_WIDTH };
